import { useEffect, useRef, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { api, streamUrl } from "../lib/api";
import { ArrowLeft, RotateCcw, Sparkles } from "lucide-react";
import Hls from "hls.js";

const PROGRESS_INTERVAL_MS = 15000;
const MIN_POSITION_TO_REPORT = 5; // seconds
const MIN_POSITION_TO_RESUME = 10; // don't bother seeking for a trivial <10s stub
const RESUME_TAIL_CUTOFF = 0.95; // if user was ≥95% done, treat as finished — start over

// Match backend `_TIER_QUALITY_CAP`. Used only to spell out the upsell nudge.
const NEXT_TIER_LABEL = {
  mobile: "HD 720p — ₦3,000/mo",
  basic: "Full HD 1080p — ₦4,000/mo",
  standard: "4K UHD HDR — ₦5,000/mo",
};

function formatTime(sec) {
  if (!Number.isFinite(sec) || sec <= 0) return "0:00";
  const s = Math.floor(sec % 60);
  const m = Math.floor((sec / 60) % 60);
  const h = Math.floor(sec / 3600);
  const pad = (n) => String(n).padStart(2, "0");
  return h > 0 ? `${h}:${pad(m)}:${pad(s)}` : `${m}:${pad(s)}`;
}

export default function Watch() {
  const { id } = useParams();
  const nav = useNavigate();
  const videoRef = useRef(null);
  const [movie, setMovie] = useState(null);
  const [error, setError] = useState("");
  const [resumeInfo, setResumeInfo] = useState(null); // { position } briefly shown after resume
  const [qualityCap, setQualityCap] = useState(null); // { max_height, tier } from playback token
  const [upsellHint, setUpsellHint] = useState(null); // { blockedHeight } when a higher variant exists

  // Refs so the progress reporter sees latest values without re-binding effects.
  const movieRef = useRef(null);
  const lastReportedRef = useRef(0);
  const savedPositionRef = useRef(0); // seconds where the viewer left off
  const resumedRef = useRef(false); // ensure we only seek once per mount
  useEffect(() => { movieRef.current = movie; }, [movie]);

  useEffect(() => {
    let active = true;
    // Reset per-movie state
    savedPositionRef.current = 0;
    resumedRef.current = false;
    setResumeInfo(null);
    (async () => {
      try {
        const { data: m } = await api.get(`/movies/${id}`);
        if (!active) return;
        // If Cloudflare-hosted, mint a signed playback URL bound to this
        // authenticated viewer. We swap the public hls_url for the signed one.
        if (m.cloudflare_uid) {
          try {
            const { data: tok } = await api.get(`/movies/${id}/playback-token`);
            m.hls_url = tok.hls_url;
            m.thumbnail_url = tok.thumbnail_url;
            // Server-derived quality cap based on the caller's subscription tier.
            // We store it so the HLS.js effect can filter levels and drive the
            // "Upgrade for higher quality" chip.
            setQualityCap({ max_height: tok.max_height || null, tier: tok.tier || null });
          } catch (e) {
            // Token mint failed — surface the actual server message.
            const det = e.response?.data?.detail;
            const code = e.response?.status;
            if (code === 402) setError("This title requires an active subscription. Manage your plan to start watching.");
            else if (code === 403) setError("This title isn't available to you right now.");
            else if (code === 503) setError("Playback isn't configured on the server. Ask an admin to check Cloudflare signing keys.");
            else setError(typeof det === "string" ? det : "Couldn't authorize playback. Please try again.");
            setMovie(m);
            return;
          }
        }
        // Fetch saved playback position (best-effort — never blocks the player).
        try {
          const { data: wp } = await api.get(`/me/watch/${id}`);
          const pos = Number(wp?.position_seconds) || 0;
          const dur = Number(wp?.duration_seconds) || 0;
          if (pos >= MIN_POSITION_TO_RESUME) {
            // If user was near the end, start over.
            if (dur > 0 && pos / dur >= RESUME_TAIL_CUTOFF) {
              savedPositionRef.current = 0;
            } else {
              savedPositionRef.current = pos;
            }
          }
        } catch { /* no saved progress → start from 0 */ }
        setMovie(m);
      } catch {
        if (active) setError("Unable to load this title.");
      }
    })();
    return () => { active = false; };
  }, [id]);

  // ---- Watch progress reporting (Continue Watching) ----
  const reportProgress = () => {
    const el = videoRef.current;
    const m = movieRef.current;
    if (!el || !m) return;
    const pos = el.currentTime || 0;
    const dur = el.duration || m.duration_seconds || 0;
    if (pos < MIN_POSITION_TO_REPORT) return;
    // Avoid spamming when the user pauses for a long time at the same position.
    if (Math.abs(pos - lastReportedRef.current) < 2) return;
    lastReportedRef.current = pos;
    api.put("/me/watch", {
      movie_id: m.id,
      position_seconds: pos,
      duration_seconds: dur,
    }).catch(() => { /* silently swallow — never block playback */ });
  };

  useEffect(() => {
    if (!movie || !videoRef.current) return;
    const el = videoRef.current;
    let hls = null;

    if (movie.hls_url) {
      // Cloudflare Stream HLS playback. We handle the three Hls.js fatal error
      // classes correctly:
      //   - NETWORK_ERROR  → call startLoad() to retry; if still failing,
      //                       fetch the manifest ourselves to surface 404/403.
      //   - MEDIA_ERROR    → call recoverMediaError().
      //   - OTHER_ERROR    → destroy and report.
      let networkRetried = false;
      let mediaRetried = false;
      const surfaceManifestStatus = async () => {
        try {
          const r = await fetch(movie.hls_url, { method: "HEAD" });
          if (r.status === 404) setError("This movie's master playlist isn't on Cloudflare yet. Try again in a minute — or contact support.");
          else if (r.status === 403) setError("Cloudflare blocked the request (403). The signed-URL / access-control config may need attention.");
          else if (r.status >= 500) setError(`Cloudflare returned ${r.status}. Try again in a moment.`);
          else setError("Playback couldn't start. Try refreshing — or contact support if this keeps happening.");
        } catch {
          setError("Playback couldn't start (network unreachable). Check your connection and try again.");
        }
      };

      if (Hls.isSupported()) {
        hls = new Hls({ enableWorker: true });
        hls.loadSource(movie.hls_url);
        hls.attachMedia(el);

        // Tier-based level cap. Fires once when the master manifest arrives.
        // We refuse to switch to any level taller than max_height, and set
        // `autoLevelCapping` so the ABR algorithm never even considers them.
        // Also detects if a higher-resolution variant *exists* on the master
        // playlist — if so, we show the subtle upsell chip in the corner.
        const maxH = qualityCap?.max_height || null;
        hls.on(Hls.Events.MANIFEST_PARSED, () => {
          if (!maxH) return;
          const levels = hls.levels || [];
          if (levels.length === 0) return;
          let capIdx = -1;
          let higherExists = false;
          let highestBlocked = 0;
          levels.forEach((lvl, i) => {
            const h = lvl.height || 0;
            if (h && h <= maxH) {
              if (capIdx === -1 || h > (levels[capIdx].height || 0)) capIdx = i;
            } else if (h > maxH) {
              higherExists = true;
              highestBlocked = Math.max(highestBlocked, h);
            }
          });
          if (capIdx >= 0) {
            hls.autoLevelCapping = capIdx;
            // Also set startLevel so the first fragment we fetch is the capped one,
            // preventing a brief flash of higher-resolution video before ABR kicks in.
            try { hls.startLevel = capIdx; } catch { /* older hls.js */ }
          }
          if (higherExists && qualityCap?.tier && NEXT_TIER_LABEL[qualityCap.tier]) {
            setUpsellHint({ blockedHeight: highestBlocked });
          }
        });

        hls.on(Hls.Events.ERROR, (_, data) => {
          // Always log so the browser console gives us the real story.
          console.warn("[hls.js error]", { type: data.type, details: data.details, fatal: data.fatal, response: data.response, url: data.url });
          if (!data.fatal) return;
          if (data.type === Hls.ErrorTypes.NETWORK_ERROR) {
            if (!networkRetried) {
              networkRetried = true;
              try { hls.startLoad(); return; } catch { /* fall through */ }
            }
            surfaceManifestStatus();
          } else if (data.type === Hls.ErrorTypes.MEDIA_ERROR) {
            if (!mediaRetried) {
              mediaRetried = true;
              try { hls.recoverMediaError(); return; } catch { /* fall through */ }
            }
            setError("This movie's stream couldn't be decoded. The source file may be corrupt — please re-upload.");
          } else {
            setError("Playback engine error — please refresh and try again.");
          }
        });
      } else if (el.canPlayType("application/vnd.apple.mpegurl")) {
        el.src = movie.hls_url;
      } else {
        setError("HLS playback not supported in this browser.");
      }
    } else if (movie.video_path) {
      el.src = streamUrl(movie.id);
    } else {
      setError("This title isn't available for streaming yet.");
    }

    // Wire progress events
    const onPause = () => reportProgress();
    const onEnded = () => reportProgress();
    el.addEventListener("pause", onPause);
    el.addEventListener("ended", onEnded);

    // Hard 30s timeout: if the player hasn't reached `loadedmetadata` by then,
    // surface an actionable message instead of letting the spinner hang.
    let metadataSeen = false;
    const onLoadedMetadata = () => {
      metadataSeen = true;
      // Resume from saved position — do this exactly once, right after
      // metadata is known so we can safely seek without racing autoplay.
      if (!resumedRef.current) {
        resumedRef.current = true;
        const pos = savedPositionRef.current;
        if (pos >= MIN_POSITION_TO_RESUME) {
          try {
            // Clamp to slightly before duration to avoid landing at the very end.
            const cap = Math.max(0, (el.duration || pos + 1) - 1);
            const target = Math.min(pos, cap);
            el.currentTime = target;
            setResumeInfo({ position: target });
            // Auto-hide the "Resumed from…" chip after 4s.
            setTimeout(() => setResumeInfo(null), 4000);
          } catch { /* ignore seek failure — player continues from 0 */ }
        }
      }
    };
    el.addEventListener("loadedmetadata", onLoadedMetadata);
    const stallTimeout = setTimeout(() => {
      if (!metadataSeen) {
        // Don't override a more-specific error already shown
        setError((prev) => prev || "Playback didn't start within 30 seconds. The video may not be ready on Cloudflare yet. An admin can run the Diagnose tool to inspect.");
      }
    }, 30000);

    // Periodic ping while playing
    const interval = setInterval(reportProgress, PROGRESS_INTERVAL_MS);

    // Final report on tab close / route change
    const beforeUnload = () => reportProgress();
    window.addEventListener("beforeunload", beforeUnload);

    return () => {
      if (hls) hls.destroy();
      el.removeEventListener("pause", onPause);
      el.removeEventListener("ended", onEnded);
      el.removeEventListener("loadedmetadata", onLoadedMetadata);
      clearInterval(interval);
      clearTimeout(stallTimeout);
      window.removeEventListener("beforeunload", beforeUnload);
      reportProgress(); // one last write on unmount
    };
  }, [movie, qualityCap]);

  return (
    <div className="bg-black min-h-[calc(100vh-4rem)] flex flex-col" data-testid="watch-page">
      <div className="max-w-[1400px] w-full mx-auto px-6 lg:px-12 py-4 flex items-center justify-between">
        <button onClick={() => nav(-1)} className="inline-flex items-center gap-2 text-sm text-white/60 hover:text-white" data-testid="watch-back-button">
          <ArrowLeft className="h-4 w-4" /> Back
        </button>
        {movie && (
          <div className="text-right">
            <p className="text-xs text-white/40">Now playing</p>
            <p className="font-display font-bold">{movie.title}</p>
          </div>
        )}
      </div>

      <div className="flex-1 flex items-center justify-center px-6 lg:px-12 pb-12">
        {error ? (
          <div className="text-center" data-testid="watch-error">
            <p className="text-white/70">{error}</p>
            <Link to="/subscription" className="ember-btn mt-4">Manage subscription</Link>
          </div>
        ) : movie ? (
          <div className="w-full max-w-[1100px] relative">
            <video
              ref={videoRef}
              controls
              autoPlay
              playsInline
              data-testid="video-player"
              className="w-full aspect-video bg-black rounded-xl border border-white/5 shadow-2xl"
              onError={() => setError("Streaming unavailable.")}
            />
            {resumeInfo && (
              <div
                className="absolute top-4 left-4 inline-flex items-center gap-2 px-3 py-2 rounded-full bg-black/70 backdrop-blur-md border border-[#E65F2B]/40 text-sm text-white shadow-lg pointer-events-none animate-in fade-in slide-in-from-top-2"
                data-testid="resume-indicator"
              >
                <RotateCcw className="h-3.5 w-3.5 text-[#E65F2B]" />
                <span>Resumed from <span className="font-bold tabular-nums">{formatTime(resumeInfo.position)}</span></span>
              </div>
            )}
            {upsellHint && qualityCap?.tier && NEXT_TIER_LABEL[qualityCap.tier] && (
              <Link
                to="/subscription"
                className="absolute top-4 right-4 inline-flex items-center gap-2 px-3 py-2 rounded-full bg-black/70 backdrop-blur-md border border-[#EAB308]/40 text-xs text-white/90 hover:text-white hover:border-[#EAB308]/80 transition shadow-lg animate-in fade-in slide-in-from-top-2"
                data-testid="quality-upsell-chip"
                title={`This film has a ${upsellHint.blockedHeight}p version — upgrade to unlock it.`}
              >
                <Sparkles className="h-3.5 w-3.5 text-[#EAB308]" />
                <span>Upgrade for <b>{NEXT_TIER_LABEL[qualityCap.tier]}</b></span>
              </Link>
            )}
          </div>
        ) : (
          <div className="h-10 w-10 rounded-full border-2 border-[#E65F2B] border-t-transparent animate-spin" />
        )}
      </div>
    </div>
  );
}
